import { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { PageBody } from '../../components/layout/PageBody';
import { ArticleEditor } from '../../components/editor/ArticleEditor';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorAlert } from '../../components/common/ErrorAlert';
import { LoadingState } from '../../components/common/LoadingState';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { RejectReasonDialog } from '../../components/common/RejectReasonDialog';
import { articleStatusLabel } from '../../utils/labels';
import { useSmartBack } from '../../hooks/useSmartBack';
import { useFeedback } from '../../components/common/FeedbackProvider';
import { useAuth } from '../../hooks/useAuth';
import { queryClient, invalidateAfterContentChange } from '../../api/queryClient';
import { contentService } from '../../services/contentService';
import {
  canAdminPublish,
  canCreate,
  canPublishApproved,
  canReviewArticle,
} from '../../utils/roles';

function extractHtml(article) {
  const textBlock = article?.blocks?.find((b) => b.type === 'text' && b.html);
  if (textBlock?.html) return textBlock.html;
  if (article?.content) return `<p>${article.content}</p>`;
  return '';
}

export function ArticleEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const goBack = useSmartBack('/admin/content');
  const { user } = useAuth();
  const role = user?.role;
  const { showSuccess, showError } = useFeedback();
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [article, setArticle] = useState(null);
  const [editorKey, setEditorKey] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [skipConfirm, setSkipConfirm] = useState(false);

  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    setLoading(true);
    contentService
      .getAdmin(id)
      .then((data) => {
        if (cancelled) return;
        setArticle(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load article');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  if (isNew && user && !canCreate(role)) {
    return <Navigate to="/admin/content" replace />;
  }

  const authorEditable =
    isNew ||
    (article &&
      (article.status === 'draft' || article.status === 'rejected') &&
      article.createdById === user?.id);

  const reviewerEditable =
    article &&
    (canReviewArticle(role, article, user?.id) ||
      (role === 'admin' &&
        (article.status === 'pending' || article.status === 'approved')));

  const canEditBody = Boolean(authorEditable || reviewerEditable);
  const showReviewActions = Boolean(
    article && canReviewArticle(role, article, user?.id),
  );
  const showPublish =
    article &&
    (canPublishApproved(role, article) ||
      (canAdminPublish(role) &&
        article.status !== 'published' &&
        article.status !== 'approved'));

  async function save(payload, { submit = false } = {}) {
    setBusy(true);
    try {
      let saved;
      if (isNew) {
        saved = await contentService.create(payload);
      } else {
        saved = await contentService.update(id, payload);
      }
      if (submit) {
        await contentService.submit(saved.id);
        await invalidateAfterContentChange(queryClient);
        showSuccess('Sent for review');
        navigate('/admin/content?status=pending', { replace: true });
        return;
      }
      await invalidateAfterContentChange(queryClient);
      showSuccess(isNew ? 'Draft saved' : 'Saved');
      if (isNew) {
        navigate(`/admin/content/${saved.id}/edit`, { replace: true });
      } else {
        setArticle(saved);
        setEditorKey((k) => k + 1);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function onApprove() {
    setBusy(true);
    try {
      const saved = await contentService.approve(id);
      setArticle(saved);
      await invalidateAfterContentChange(queryClient);
      showSuccess('Ready to publish');
      navigate('/admin/content?status=approved', { replace: true });
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Submit for publish failed');
    } finally {
      setBusy(false);
    }
  }

  async function onReject(note) {
    setBusy(true);
    setRejectOpen(false);
    try {
      const saved = await contentService.reject(id, note);
      setArticle(saved);
      await invalidateAfterContentChange(queryClient);
      showSuccess('Sent back for changes');
      navigate('/admin/content?status=rejected', { replace: true });
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Reject failed');
    } finally {
      setBusy(false);
    }
  }

  async function onPublish() {
    setBusy(true);
    try {
      const saved = await contentService.publish(id);
      setArticle(saved);
      await invalidateAfterContentChange(queryClient);
      showSuccess('Published to feed');
      navigate(`/article/${saved.id || id}`, {
        replace: true,
        state: { from: '/admin/content?status=published' },
      });
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell
      title={isNew ? 'New story' : loading ? 'Edit story' : article ? 'Edit story' : 'Story'}
      subtitle={isNew ? 'Write a draft' : article ? articleStatusLabel(article.status) : 'Loading…'}
      maxWidth="md"
    >
      <PageBody>
        <ErrorAlert
          message={!loading && !article && !isNew ? null : error}
          onClose={() => setError(null)}
        />
        {loading && <LoadingState label="Loading article…" />}

        {!loading && !isNew && !article && (
          <EmptyState
            title="This story was removed"
            description="There is nothing left to edit. The old alert is still in Alerts, but the story is gone from Content."
            action={
              <Button variant="outlined" onClick={goBack}>
                Back to content
              </Button>
            }
          />
        )}

        {!loading && article && role === 'admin' && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Attribution (admin only)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created by: {article.createdByName || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Approved by: {article.approvedByName || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Published by: {article.publishedByName || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Status: {articleStatusLabel(article.status)}
            </Typography>
          </Box>
        )}

        {!loading && canEditBody && (
          <ArticleEditor
            key={editorKey}
            initialTitle={article?.title ?? ''}
            initialSummary={article?.summary ?? ''}
            initialHtml={extractHtml(article)}
            initialCategories={article?.categories}
            busy={busy}
            onSaveDraft={(payload) => void save(payload)}
            onDirtyChange={setDirty}
            onSubmit={
              authorEditable
                ? (payload) => void save(payload, { submit: true })
                : undefined
            }
          />
        )}

        {!loading && !canEditBody && article && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Viewing “{article.title}” ({articleStatusLabel(article.status)}).
          </Typography>
        )}

        {!loading && article && dirty && canEditBody && (
          <Typography color="text.secondary" sx={{ mt: 1.5 }}>
            Save your edits before sending this on.
          </Typography>
        )}

        {!loading && article && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {showReviewActions && (
              <>
                <Button
                  variant="contained"
                  disabled={busy || dirty}
                  onClick={() => void onApprove()}
                >
                  Submit for publish
                </Button>
                <Button
                  color="error"
                  variant="outlined"
                  disabled={busy || dirty}
                  onClick={() => setRejectOpen(true)}
                >
                  Reject
                </Button>
              </>
            )}
            {showPublish && (
              <Button
                variant="contained"
                color="secondary"
                disabled={busy || dirty}
                onClick={() => {
                  if (article.status !== 'approved') {
                    setSkipConfirm(true);
                    return;
                  }
                  void onPublish();
                }}
              >
                {article.status === 'approved'
                  ? 'Publish to feed'
                  : 'Skip review and publish'}
              </Button>
            )}
            <Button type="button" onClick={goBack} variant="outlined">
              Back to content
            </Button>
          </Stack>
        )}
        <ConfirmDialog
          open={skipConfirm}
          title="Skip review and publish?"
          message="This goes live without a reviewer. Prefer Submit for publish when you can."
          confirmLabel="Publish now"
          onClose={() => setSkipConfirm(false)}
          onConfirm={() => {
            setSkipConfirm(false);
            void onPublish();
          }}
        />
        <RejectReasonDialog
          open={rejectOpen}
          busy={busy}
          onClose={() => setRejectOpen(false)}
          onConfirm={(note) => void onReject(note)}
        />
      </PageBody>
    </AppShell>
  );
}
